'use client';

import { getCardsByUser } from '@api/card-api';
import CardView from '@components/Card/CardView';
import QRCode from '@components/QRCode/QRCode';
import { useUser } from '@context/user-context';
import useMedia from '@hooks/use-media';
import useOnce from '@hooks/use-once';
import { useOpenAppUrl } from '@hooks/use-open-app-url';
import { analytics } from '@modules/analytics';
import { clearSignUpType } from '@modules/auth-helpers';
import { Modal } from '@ui-lib';
import { useRouter } from 'next/router';
import { useState } from 'react';
import type { Card as CardType } from 'types';

import * as styles from './WelcomeAppExperience.css';

const WELCOME_APP_QUERY_KEY = 'welcomeApp';

const CloseIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 3L13 13M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ArrowRightIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 8H13M13 8L8.5 3.5M13 8L8.5 12.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const WelcomeAppExperience: React.FC = () => {
  const router = useRouter();
  const { user } = useUser();
  const { appStore } = useOpenAppUrl({ includeOpaqueToken: true, useCase: 'signup' });

  const isMobile = useMedia<'mobile' | 'desktop'>(['(max-width: 700px)'], ['mobile'], 'desktop');

  const [card, setCard] = useState<CardType>();
  const [loadingCard, setLoadingCard] = useState(true);

  const isOpen = router.isReady && router.query[WELCOME_APP_QUERY_KEY] === '1';

  useOnce(
    (preventSecondRun) => {
      if (!isOpen || !user?.userId) return;
      preventSecondRun();
      (async () => {
        const cards = await getCardsByUser(user.userId);
        setCard(cards[0]);
        setLoadingCard(false);
      })();
    },
    [isOpen, user?.userId],
  );

  function dismiss() {
    clearSignUpType();
    const { [WELCOME_APP_QUERY_KEY]: _omit, ...restQuery } = router.query;
    router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
  }

  function handleGoToDashboard() {
    analytics.track({ name: 'onboarding_gotodashboardbutton_clicked' });
    dismiss();
  }

  function handleGetTheApp() {
    analytics.track({ name: 'onboarding_getappbutton_clicked' });
    window.location.assign(appStore);
  }

  if (!isOpen) return null;

  if (isMobile === 'mobile') {
    return (
      <div className={`${styles.root} ${styles.sheet}`} role="dialog" aria-modal="true">
        <div className={styles.sheetHeader}>
          <button type="button" className={styles.closeButton} onClick={dismiss} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <div className={styles.sheetContent}>
          <h2 className={styles.sheetHeading}>
            Welcome to Blinq,
            <br />
            Use the app to start sharing
          </h2>
          <div className={styles.sheetCardView}>
            <CardView card={card} loading={loadingCard} mode="standalone" />
          </div>
          <p className={styles.sheetBody}>
            Get the app to add your logo, change the color and add your socials, website and more.
          </p>
          <button type="button" className={styles.primaryButton} onClick={handleGetTheApp}>
            Get the app
            <ArrowRightIcon />
          </button>
        </div>
      </div>
    );
  }

  return (
    <Modal open={isOpen} handleClose={dismiss}>
      <div className={`${styles.root} ${styles.modal}`}>
        <div className={styles.modalHeader}>
          <button type="button" className={styles.closeButton} onClick={dismiss} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalContent}>
          <h2 className={styles.modalHeading}>
            Welcome to Blinq,
            <br />
            Use the app to start sharing
          </h2>
          <div className={styles.modalQr}>
            <QRCode value={appStore} purpose="login" size={168} errorCorrectionLevel="M" />
          </div>
          <p className={styles.modalBody}>
            For quick access to your card you can download the Blinq app by scanning the QR code
          </p>
          <button type="button" className={styles.primaryButton} onClick={handleGoToDashboard}>
            Go to dashboard
            <ArrowRightIcon />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default WelcomeAppExperience;
